import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Key,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  ExternalLink,
  AlertCircle,
  AlertTriangle,
  Plus,
  X,
} from 'lucide-react';
import { getAuthHeaders } from '@/services/authConfig';

interface ProviderField {
  name: string;
  label: string;
  type: 'text' | 'password';
  placeholder?: string;
  default?: string;
}

interface Provider {
  value: string;
  label: string;
  description: string;
  credentialType: 'simple' | 'complex';
  fields: ProviderField[];
  helpUrl?: string;
  recommended?: boolean;
}

const PROVIDERS: Provider[] = [
  {
    value: 'openrouter',
    label: 'OpenRouter',
    description: 'Access 200+ models with unified billing',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password', placeholder: 'sk-or-v1-...' }],
    helpUrl: 'https://openrouter.ai/keys',
    recommended: true,
  },
  {
    value: 'openai',
    label: 'OpenAI (Direct)',
    description: 'GPT-4, GPT-5, and more',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password', placeholder: 'sk-...' }],
    helpUrl: 'https://platform.openai.com/api-keys',
  },
  {
    value: 'anthropic',
    label: 'Anthropic (Direct)',
    description: 'Claude models',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password', placeholder: 'sk-ant-...' }],
    helpUrl: 'https://console.anthropic.com/settings/keys',
  },
  {
    value: 'google',
    label: 'Google (Direct)',
    description: 'Gemini models',
    credentialType: 'simple',
    fields: [{ name: 'api_key', label: 'API Key', type: 'password' }],
    helpUrl: 'https://aistudio.google.com/apikey',
  },
  {
    value: 'bedrock',
    label: 'AWS Bedrock',
    description: 'Claude, Llama, Titan via AWS',
    credentialType: 'complex',
    fields: [
      { name: 'aws_access_key_id', label: 'AWS Access Key ID', type: 'text' },
      { name: 'aws_secret_access_key', label: 'AWS Secret Access Key', type: 'password' },
      { name: 'aws_region', label: 'AWS Region', type: 'text', default: 'us-east-1', placeholder: 'us-east-1' },
    ],
  },
  {
    value: 'azure',
    label: 'Azure OpenAI',
    description: 'Azure-hosted OpenAI models',
    credentialType: 'complex',
    fields: [
      { name: 'endpoint', label: 'Endpoint URL', type: 'text', placeholder: 'https://your-resource.openai.azure.com/' },
      { name: 'api_key', label: 'API Key', type: 'password' },
      { name: 'api_version', label: 'API Version', type: 'text', default: '2024-02-15-preview' },
    ],
  },
];

interface ApiKeySetupStepProps {
  teamId?: string;
  onComplete: (skipped: boolean, configuredProviders?: string[]) => void;
}

export function ApiKeySetupStep({ teamId, onComplete }: ApiKeySetupStepProps) {
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showKeyValues, setShowKeyValues] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [configuredProviders, setConfiguredProviders] = useState<string[]>([]);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [showAddMoreDialog, setShowAddMoreDialog] = useState(false);

  // Build headers with team ID
  const getHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };
    if (teamId) {
      headers['X-Team-Id'] = teamId;
      headers['X-Group-Id'] = teamId;
    }
    return headers;
  };

  const handleProviderSelect = (provider: Provider) => {
    setSelectedProvider(provider);
    // Initialize with defaults
    const defaults: Record<string, string> = {};
    provider.fields.forEach(field => {
      if (field.default) defaults[field.name] = field.default;
    });
    setFormData(defaults);
    setShowKeyValues({});
    setError(null);
  };

  const handleSaveCredentials = async () => {
    if (!selectedProvider) return;

    // Validate required fields
    for (const field of selectedProvider.fields) {
      const value = formData[field.name];
      if (!field.default && (!value || !value.trim())) {
        setError(`${field.label} is required`);
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Save the credentials
      const saveResponse = await fetch('/api/settings/api-keys', {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          provider: selectedProvider.value,
          ...formData,
          name: `${selectedProvider.label} (Onboarding)`,
        }),
      });

      if (!saveResponse.ok) {
        const data = await saveResponse.json();
        throw new Error(data.detail || 'Failed to save credentials');
      }

      // Test the credentials
      const testResponse = await fetch(`/api/settings/api-keys/${selectedProvider.value}/test`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
      });

      const testData = await testResponse.json();

      if (testData.valid === false) {
        // Delete invalid credentials
        await fetch(`/api/settings/api-keys/${selectedProvider.value}`, {
          method: 'DELETE',
          headers: getHeaders(),
          credentials: 'include',
        });
        throw new Error(testData.message || 'Credentials validation failed');
      }

      // Success - add to configured providers
      const newConfiguredProviders = [...configuredProviders, selectedProvider.value];
      setConfiguredProviders(newConfiguredProviders);
      setSelectedProvider(null);
      setFormData({});

      // Ask if user wants to add more providers
      setShowAddMoreDialog(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to save credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipClick = () => {
    setShowSkipDialog(true);
  };

  const handleConfirmSkip = () => {
    setShowSkipDialog(false);
    onComplete(true, []);
  };

  const toggleFieldVisibility = (fieldName: string) => {
    setShowKeyValues(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          <Key className="h-12 w-12 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Configure API Keys</h2>
        <p className="text-sm text-muted-foreground">
          Add at least one provider to enable AI features. Additional providers are optional.
        </p>
      </div>

      {/* Configured providers badges */}
      {configuredProviders.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center">
          {configuredProviders.map(providerValue => {
            const provider = PROVIDERS.find(p => p.value === providerValue);
            return (
              <Badge key={providerValue} variant="secondary" className="gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {provider?.label || providerValue}
              </Badge>
            );
          })}
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!selectedProvider ? (
        // Provider selection
        <div className="space-y-3">
          <Label>Select a provider to configure:</Label>
          <div className="grid gap-2">
            {PROVIDERS.filter(p => !configuredProviders.includes(p.value)).map(provider => (
              <button
                key={provider.value}
                onClick={() => handleProviderSelect(provider)}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors text-left"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{provider.label}</span>
                    {provider.recommended && (
                      <Badge variant="default" className="text-xs">Recommended</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{provider.description}</p>
                </div>
                <Plus className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        // Credential form for selected provider
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base">{selectedProvider.label}</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedProvider(null);
                setFormData({});
                setError(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {selectedProvider.fields.map(field => (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name}>
                {field.label}
                {field.default && <span className="text-muted-foreground ml-1">(optional)</span>}
              </Label>
              <div className="relative">
                <Input
                  id={field.name}
                  type={field.type === 'password' && !showKeyValues[field.name] ? 'password' : 'text'}
                  placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  disabled={isSubmitting}
                />
                {field.type === 'password' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => toggleFieldVisibility(field.name)}
                  >
                    {showKeyValues[field.name] ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          ))}

          {selectedProvider.helpUrl && (
            <div className="flex items-center gap-2 text-sm">
              <a
                href={selectedProvider.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                Get {selectedProvider.label} API key
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          <Button
            onClick={handleSaveCredentials}
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving & Testing...
              </>
            ) : (
              'Save & Test'
            )}
          </Button>
        </div>
      )}

      {/* Skip button - only show if no providers configured yet */}
      {configuredProviders.length === 0 && !selectedProvider && (
        <div className="pt-4 border-t">
          <button
            onClick={handleSkipClick}
            className="w-full text-sm text-muted-foreground hover:text-foreground"
          >
            Skip for now (you can add this later in Settings)
          </button>
        </div>
      )}

      {/* Skip warning dialog */}
      <AlertDialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Skip API Key Setup?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Without API keys configured, you won't be able to:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Run AI agents or workflows</li>
                <li>Use the assistant chat</li>
                <li>Execute evaluations</li>
              </ul>
              <p className="pt-2">
                You can add API keys anytime in <strong>Settings → API Keys</strong>.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSkip}>
              Skip and Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add more providers dialog */}
      <AlertDialog open={showAddMoreDialog} onOpenChange={setShowAddMoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              API Key Added Successfully!
            </AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to add another provider? This is optional - you can always add more later in Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowAddMoreDialog(false);
              onComplete(false, configuredProviders);
            }}>
              No, Continue
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => setShowAddMoreDialog(false)}>
              Add Another Provider
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
